import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import CreateTaskSchedule, {
  CreateTaskScheduleChangeEvent,
} from "@coworker/apprestructured/src/task-management/components/ScheduleTask/CreateTaskSchedule/CreateTaskSchedule";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { TaskCreatorAndEditorSheetContent } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorAndEditorSheetContent/TaskCreatorAndEditorSheetContent";
import { TaskCreatorAndEditorSheetFooter } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorAndEditorSheetFooter/TaskCreatorAndEditorSheetFooter";
import { LOADING } from "@coworker/apprestructured/src/task-management/loading";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import useCreateInitialTask from "@coworker/apprestructured/src/task-management/services/useCreateInitialTask";
import { useCreateSchedule } from "@coworker/apprestructured/src/task-management/services/useCreateSchedule";
import { useCreateTask } from "@coworker/apprestructured/src/task-management/services/useCreateTask";
import { useParsedInitialValues } from "@coworker/apprestructured/src/task-management/services/useParsedInitialValues";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import {
  CreateSchedule,
  Recurrence,
} from "@coworker/apprestructured/src/task-management/types/Schedule";
import {
  CreateTask,
  Task,
} from "@coworker/apprestructured/src/task-management/types/Task";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import { useFileUploader } from "@coworker/apprestructured/src/uploader/useFileUploader";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useTranslation } from "@coworker/locales";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAfterCreate } from "../../../services/useAfterCreate";
import { useNotificationsState } from "../../Notifications/hooks/useNotificationsState";
import { Notifications } from "../../Notifications/Notifications";
import { NotificationsProvider } from "../../Notifications/NotificationsContext";
import SomethingWentWrongPrompt from "../../SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import styles from "./TaskCreatorSheet.module.css";

/**
 * The TaskCreatorSheet is open for the task type with id 'taskCreator' taken
 * from the url search params. Open/close the sheet by adding/removing a
 * 'taskCreator' search param, or use the 'useSearchParamState' hook to reduce
 * some boilerplate.
 */
function TaskCreatorSheet() {
  const [{ taskCreator: taskTypeId, initialValues }, setSearchParams] =
    useSearchParamState();
  const [taskType, setTaskType] = useState<TaskType | null>(null);
  const [task, setTask] = useState<Record<string, unknown> | null>(null);
  const [taskTypeNotFound, setTaskTypeNotFound] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [images, setImages] = useState<NewOrExistingFile[]>([]);
  const [files, setFiles] = useState<NewOrExistingFile[]>([]);
  const [scheduleStart, setScheduleStart] = useState<string | null>(null);
  const [scheduleEnd, setScheduleEnd] = useState<string | null>(null);
  const [scheduleRecurrence, setScheduleRecurrence] =
    useState<Recurrence | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isInvalidInitialValues, setIsInvalidInitialValues] = useState(false);
  const { t } = useTranslation();
  const { mutateAsync: createTaskMutation, error: createTaskError } =
    useCreateTask();
  const { mutateAsync: createScheduleMutation, error: createScheduleError } =
    useCreateSchedule();
  const { data: taskTypes } = useAllTaskTypes();

  const notifications = useNotificationsState();
  const { showError, clearAll } = notifications;

  const createInitialTask = useCreateInitialTask();

  const executeAfterCreate = useAfterCreate(task);

  // Parse initialValues from query params
  const parseInitialValues = useParsedInitialValues();
  const parsedInitialValuesResult = useMemo(() => {
    if (initialValues) {
      return parseInitialValues(initialValues);
    }
    return { initialValues: null, isInvalid: false };
  }, [initialValues, parseInitialValues]);

  const handleTaskTypeIdChange = useEffectEvent(
    (taskTypeId: string, taskTypes: TaskType[]) => {
      clearAll();
      setIsCreating(false);
      const taskType = taskTypes.find(({ id }) => id === taskTypeId);
      setTaskTypeNotFound(!taskType);
      if (taskType) {
        if (parsedInitialValuesResult.isInvalid) {
          setIsInvalidInitialValues(true);
        } else {
          setIsInvalidInitialValues(false);
          setTaskType(taskType);
          setTask(
            createInitialTask(taskType, parsedInitialValuesResult.initialValues)
          );
          setShowValidation(false);
          setImages([]);
          setFiles([]);
          setScheduleStart("");
          setScheduleEnd("");
          setScheduleRecurrence(null);
        }
      }
    }
  );
  useEffect(() => {
    if (taskTypeId && taskTypes) {
      handleTaskTypeIdChange(taskTypeId, taskTypes);
    }
  }, [taskTypeId, taskTypes]);

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard();

  const creatorFragmentRefs = useRef<Map<string, CreatorFragmentHandle>>(
    new Map()
  );

  const setCreatorFragmentRef = useCallback(
    (key: string) => (element: CreatorFragmentHandle | null) => {
      if (element) {
        creatorFragmentRefs.current.set(key, element);
      } else {
        creatorFragmentRefs.current.delete(key);
      }
    },
    []
  );

  const {
    ready: imageUploaderIsReady,
    upload: uploadImages,
    createUploads: imagesCreateUploads,
    error: imageUploaderError,
  } = useImageUploader(images);

  const {
    ready: fileUploaderIsReady,
    upload: uploadFiles,
    createUploads: filesCreateUploads,
    error: fileUploaderError,
  } = useFileUploader(files);

  const queryClient = useQueryClient();

  const handleMutationResults = useCallback(
    async (
      createdTaskResult: PromiseSettledResult<Task>,
      imageUploadResult: PromiseSettledResult<void>,
      fileUploadResult: PromiseSettledResult<void>
    ) => {
      handleImageUploadResult(imageUploadResult, createdTaskResult);
      handleFileUploadResult(fileUploadResult, createdTaskResult);
      if (imageUploadResult.status === "rejected") {
        showError({
          title: t("imageUploadFailedTitleString"),
          text: t("imageUploadFailedTextString"),
        });
      }
      if (fileUploadResult.status === "rejected") {
        showError({
          title: t("fileUploadFailedTitleString"),
          text: t("fileUploadFailedTextString"),
        });
      }
      if (createdTaskResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setSearchParams((prev) => {
          prev.delete("taskCreator");
          prev.delete("initialValues");
          prev.set("taskViewer", String(createdTaskResult.value["id"]));
        });
        await executeAfterCreate(createdTaskResult.value as Task);
      }
    },
    [
      disableSaveGuard,
      queryClient,
      setSearchParams,
      executeAfterCreate,
      showError,
      t,
    ]
  );

  const createTask = useCallback(() => {
    async function fn() {
      const createTask = {
        ...task,
        images: imagesCreateUploads,
        files: filesCreateUploads,
      } as CreateTask;
      const [createdTaskResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          createTaskMutation(createTask),
          uploadImages(),
          uploadFiles(),
        ]);
      await handleMutationResults(
        createdTaskResult,
        imageUploadResult,
        fileUploadResult
      );
    }

    fn();
  }, [
    createTaskMutation,
    filesCreateUploads,
    handleMutationResults,
    imagesCreateUploads,
    task,
    uploadFiles,
    uploadImages,
  ]);

  const createSchedule = useCallback(() => {
    async function fn() {
      const {
        priority,
        details,
        task_type_id,
        creator_user_id,
        assigned_user_id,
        assigned_team_id,
        store_id,
        links,
      } = task as CreateTask;
      const createSchedule: CreateSchedule = {
        start: scheduleStart!,
        end: scheduleEnd!,
        recurrence: scheduleRecurrence!,
        task: {
          priority,
          details,
          task_type_id,
          creator_user_id,
          assigned_user_id,
          assigned_team_id,
          store_id,
          images: imagesCreateUploads,
          files: filesCreateUploads,
          links,
        },
      };
      const [createdTaskResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          createScheduleMutation(createSchedule),
          uploadImages(),
          uploadFiles(),
        ]);
      handleMutationResults(
        createdTaskResult,
        imageUploadResult,
        fileUploadResult
      );
    }

    fn();
  }, [
    createScheduleMutation,
    filesCreateUploads,
    handleMutationResults,
    imagesCreateUploads,
    scheduleEnd,
    scheduleRecurrence,
    scheduleStart,
    task,
    uploadFiles,
    uploadImages,
  ]);

  const create = useCallback(() => {
    setIsCreating(true);
    if (scheduleRecurrence == null) {
      createTask();
    } else {
      createSchedule();
    }
  }, [createSchedule, createTask, scheduleRecurrence]);

  const handleCreateClick = useCallback(() => {
    const someFragmentsAreInvalid = creatorFragmentRefs.current
      .values()
      .some((creatorFragmentHandle) => {
        return !creatorFragmentHandle.valid;
      });

    if (someFragmentsAreInvalid) {
      setShowValidation(someFragmentsAreInvalid);
    } else {
      create();
    }
  }, [create]);

  const handleCloseClick = useCallback(() => {
    clearAll();
    setSearchParams((prev) => {
      prev.delete("taskCreator");
      prev.delete("initialValues");
      prev.restoreReturnTo();
    });
  }, [clearAll, setSearchParams]);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      enableSaveGuard();
      setTask((prev) => {
        if (!prev) {
          return prev;
        }
        return merge(prev, part);
      });
    },
    [enableSaveGuard]
  );

  const handleImagesChange = useCallback(
    (newImages: NewOrExistingFile[]) => {
      enableSaveGuard();
      setImages(newImages);
    },
    [enableSaveGuard]
  );

  const handleFilesChange = useCallback(
    (newFiles: NewOrExistingFile[]) => {
      enableSaveGuard();
      setFiles(newFiles);
    },
    [enableSaveGuard]
  );

  const blockInput = isCreating || !taskTypeId || !taskType;

  const creatorFragmentCommonProps: CreatorFragmentCommonProps = useMemo(() => {
    return {
      blockInput,
      task: task ? task : LOADING,
      onTaskChange: handleTaskChange,
      showValidation,
      taskType: taskType ? taskType : LOADING,
    };
  }, [blockInput, handleTaskChange, showValidation, task, taskType]);

  const handleScheduleChange = useCallback(
    ({
      futureStart,
      repeatingStart,
      end,
      recurrence,
    }: CreateTaskScheduleChangeEvent) => {
      handleTaskChange({ start: futureStart });
      setScheduleStart(repeatingStart);
      setScheduleEnd(end);
      setScheduleRecurrence(recurrence);
    },
    [handleTaskChange]
  );

  const handleRefreshClick = useCallback(() => {
    disableSaveGuard();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }, [disableSaveGuard]);

  const showSomethingWentWrongPrompt: boolean =
    !!createTaskError ||
    !!createScheduleError ||
    !!fileUploaderError ||
    !!imageUploaderError;

  const scheduleComponent = useMemo(() => {
    return (
      <CreateTaskSchedule
        disabled={blockInput}
        futureStart={(task?.["start"] || "") as string}
        onChange={handleScheduleChange}
        repeatingStart={scheduleStart}
        end={scheduleEnd}
        recurrence={scheduleRecurrence}
      />
    );
  }, [
    scheduleEnd,
    scheduleRecurrence,
    scheduleStart,
    handleScheduleChange,
    blockInput,
    task,
  ]);

  const isError = taskTypeNotFound || isInvalidInitialValues;
  const errorTitle = t("sorryTaskCantBeCreatedString");
  const errorMessage = taskTypeNotFound
    ? t("thisTaskTypeDoesNotExistString")
    : t("taskCannotBeCreatedWithPreFilledValuesString");

  return (
    <NotificationsProvider value={notifications}>
      <FixaModal
        visible={!!taskTypeId}
        handleCloseBtn={handleCloseClick}
        escapable={false}
        onKeyDown={(e) => {
          // Route Escape through the same handler as the close (X) button
          if (e.key !== "Escape") {
            return;
          }
          // Only handle Escape that belongs to THIS (outer) modal.
          const target = e.target as HTMLElement;
          if (target.closest?.(".modal-wrapper") !== e.currentTarget) {
            return;
          }
          e.preventDefault();
          handleCloseClick();
        }}
        data-testid={"task-creator-sheet"}
      >
        <FixaSheets
          labelledById="task-management-task-creator-header"
          fullSize
          className={isError ? "" : styles["greyBackground"]}
          header={
            <FixaModalHeader
              id="task-management-task-creator-header"
              closeBtnClick={handleCloseClick}
              title={isError ? "" : t(taskType?.label ?? "createString")}
            />
          }
          footer={
            isError ? null : (
              <TaskCreatorAndEditorSheetFooter
                testId={"task-creator-create-button"}
                text={`${t("create4String")} ${taskType?.label ? t(taskType.label) : ""}`}
                onClick={handleCreateClick}
                isDisabled={
                  !taskTypeId ||
                  !taskType ||
                  !imageUploaderIsReady ||
                  !fileUploaderIsReady
                }
                isLoading={isCreating}
              />
            )
          }
        >
          <TaskCreatorAndEditorSheetContent
            isError={isError}
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            creatorFragmentCommonProps={creatorFragmentCommonProps}
            isLoadingCreatorFragments={!taskType}
            detailsCreatorFragments={taskType ? taskType.creator_fragments : []}
            scheduleComponent={scheduleComponent}
            setCreatorFragmentRef={setCreatorFragmentRef}
            images={images}
            onImagesChange={handleImagesChange}
            isDisabled={blockInput}
            files={files}
            onFilesChange={handleFilesChange}
          />
        </FixaSheets>
      </FixaModal>
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
      <Notifications />
    </NotificationsProvider>
  );
}

function handleImageUploadResult(
  imageUploadResult: PromiseSettledResult<void>,
  createdTaskResult: PromiseSettledResult<Task>
) {
  if (imageUploadResult.status === "rejected") {
    const imageUploadIds =
      createdTaskResult.status === "fulfilled"
        ? createdTaskResult.value.image_ids
        : "unknown";
    reportMessageToSentry(
      "Image upload failed",
      { uploadIds: imageUploadIds },
      "error"
    );
  }
}

function handleFileUploadResult(
  fileUploadResult: PromiseSettledResult<void>,
  createdTaskResult: PromiseSettledResult<Task>
) {
  if (fileUploadResult.status === "rejected") {
    const fileUploadIds =
      createdTaskResult.status === "fulfilled"
        ? createdTaskResult.value.file_ids
        : "unknown";
    reportMessageToSentry(
      "File upload failed",
      { uploadIds: fileUploadIds },
      "error"
    );
  }
}

export default TaskCreatorSheet;
